import { api } from "#/lib/api-client";
import type {
  DepartmentCreateRequest,
  DepartmentPageResponse,
  DepartmentResponse,
  DepartmentUpdateRequest,
} from "./types";

type ListParams = {
  page?: number;
  size?: number;
  search?: string;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizePage<T>(raw: any): DepartmentPageResponse {
  const body = raw?.content !== undefined ? raw : (raw?.data ?? raw);
  const meta = body?.page ?? body;
  return {
    content:       body?.content       ?? [],
    totalElements: meta?.totalElements ?? meta?.total_elements ?? meta?.total ?? 0,
    totalPages:    meta?.totalPages    ?? meta?.total_pages    ?? 0,
    number:        meta?.number        ?? 0,
    size:          meta?.size          ?? 0,
    first:         meta?.number === 0,
    last:          (meta?.number ?? 0) + 1 >= (meta?.totalPages ?? meta?.total_pages ?? 1),
  };
}

export const departmentService = {
  async list(params: ListParams = {}): Promise<DepartmentPageResponse> {
    const { data } = await api.get("/api/hr/departments", { params });
    return normalizePage<DepartmentResponse>(data);
  },

  async getById(id: number): Promise<DepartmentResponse> {
    const { data } = await api.get<DepartmentResponse>(`/api/hr/departments/${id}`);
    return data;
  },

  async create(payload: DepartmentCreateRequest): Promise<DepartmentResponse> {
    const { data } = await api.post<DepartmentResponse>("/api/hr/departments", payload);
    return data;
  },

  async update(id: number, payload: DepartmentUpdateRequest): Promise<DepartmentResponse> {
    const { data } = await api.put<DepartmentResponse>(`/api/hr/departments/${id}`, payload);
    return data;
  },

  /** Permanent delete. Blocked by the backend (400) if the department has any employees,
   *  users, or records attached — callers should catch that and offer deactivate() instead. */
  async remove(id: number): Promise<void> {
    await api.delete(`/api/hr/departments/${id}`);
  },

  /** Soft delete — hides the department from the active list. Always safe, never blocked;
   *  the department stays editable and reactivate() brings it back at any time. */
  async deactivate(id: number): Promise<void> {
    await api.put(`/api/hr/departments/${id}/deactivate`);
  },

  async reactivate(id: number): Promise<void> {
    await api.put(`/api/hr/departments/${id}/activate`);
  },

  /** Assign (or clear) the head of a department.
   *  Backend expects { headUserId } — a system user ID, not an employee ID. */
  async assignHead(departmentId: number, headUserId: number | null): Promise<DepartmentResponse> {
    const { data } = await api.put<DepartmentResponse>(
      `/api/hr/departments/${departmentId}/assign-head`,
      { headUserId },
    );
    return data;
  },

  /** Users list for the head-of-department picker.
   *  Prefers the linked employee's full name (first + last) for display. */
  async userOptions(): Promise<{ id: number; fullName: string; username: string }[]> {
    const { data } = await api.get("/api/hr/users", { params: { size: 100 } });
    // Handle envelope { data: { content: [...] } } and flat { content: [...] }
    const body = data?.content !== undefined ? data : (data?.data ?? data);
    const list: any[] = body?.content ?? [];
    return list.map((u) => {
      // Try employee sub-record first (first + last name), then user fullName, then username
      const emp = u.employee;
      const fullName =
        (emp
          ? [emp.firstName, emp.middleName, emp.lastName].filter(Boolean).join(" ").trim()
          : null) ||
        u.fullName?.trim() ||
        u.username ||
        String(u.id);
      return {
        id: u.id,
        username: u.username ?? "",
        fullName,
      };
    });
  },
};